import React, { useState } from 'react';
import {
  Box, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, Select, MenuItem, FormControl, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import { PreviewRow, RowDecision } from '../../../services/bypassApi';

interface Props {
  rows: PreviewRow[];
  decisions: Record<number, RowDecision>;
  onDecisionChange: (rowIndex: number, decision: RowDecision) => void;
}

const VALUE_TYPES: Array<RowDecision['newParam'] extends infer T ? (T extends { valueType: infer V } ? V : never) : never> =
  ['numeric', 'text', 'alphanumeric', 'range'];

const MappingPreview: React.FC<Props> = ({ rows, decisions, onDecisionChange }) => {
  const [addNewDialog, setAddNewDialog] = useState<{ rowIndex: number; row: PreviewRow } | null>(null);
  const [newParam, setNewParam] = useState<{ standardizedName: string; valueType: string; unit: string; category: string }>({
    standardizedName: '', valueType: 'numeric', unit: '', category: ''
  });

  const openAddNew = (rowIndex: number, row: PreviewRow) => {
    setNewParam({
      standardizedName: row.parameterName,
      valueType: 'numeric',
      unit: row.unit || '',
      category: row.category || ''
    });
    setAddNewDialog({ rowIndex, row });
  };

  const confirmAddNew = () => {
    if (!addNewDialog) return;
    if (!newParam.standardizedName.trim()) return;
    onDecisionChange(addNewDialog.rowIndex, {
      action: 'addNew',
      newParam: {
        standardizedName: newParam.standardizedName.trim(),
        unit: newParam.unit.trim() || null,
        category: newParam.category.trim() || null,
        valueType: newParam.valueType as any
      }
    });
    setAddNewDialog(null);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        Mapping preview ({rows.length} parameter rows)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Rows already matched against the masters are shown with ✓. Unmatched rows need a decision before submitting.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>CSV row</TableCell>
              <TableCell>Value / Unit / Range</TableCell>
              <TableCell>Match status</TableCell>
              <TableCell>Decision</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const dec = decisions[r.rowIndex];
              const resolved = !!dec;
              return (
                <TableRow key={r.rowIndex} sx={{ '& td': { verticalAlign: 'top' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.parameterName}</Typography>
                    <Typography variant="caption" color="text.secondary">line {r.lineNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.value} {r.unit && <Chip label={r.unit} size="small" />}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.referenceRange || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.match ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="body2">
                          {r.match.standardizedName}
                          <Chip label={`T${r.match.tier} ${r.match.source}`} size="small" sx={{ ml: 0.5 }} />
                        </Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <WarningAmberIcon color="warning" fontSize="small" />
                        <Typography variant="body2" color="warning.main">No master match</Typography>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 320 }}>
                    {r.match ? (
                      <Chip label="Auto-mapped" size="small" color="success" variant="outlined" />
                    ) : (
                      <Box>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={dec?.action === 'map' ? `map:${dec.masterId || dec.vendorRowId}` : (dec?.action === 'addNew' ? 'addNew' : '')}
                            displayEmpty
                            onChange={(e) => {
                              const v = String(e.target.value);
                              if (v === 'addNew') return openAddNew(r.rowIndex, r);
                              if (v.startsWith('map:')) {
                                const id = v.slice(4);
                                const s = r.suggestions.find(s => (s.masterId === id || s.vendorRowId === id));
                                if (s) {
                                  onDecisionChange(r.rowIndex, {
                                    action: 'map',
                                    masterId: s.masterId,
                                    vendorRowId: s.vendorRowId,
                                    standardizedName: s.standardizedName
                                  });
                                }
                              }
                            }}
                            renderValue={(v) => {
                              if (!v) return <em style={{ color: '#999' }}>Pick a decision</em>;
                              if (v === 'addNew') return `+ Add as new: ${dec?.newParam?.standardizedName || r.parameterName}`;
                              const id = String(v).slice(4);
                              const s = r.suggestions.find(s => (s.masterId === id || s.vendorRowId === id));
                              return s ? `Map → ${s.standardizedName}` : 'Map → …';
                            }}
                          >
                            {r.suggestions.length === 0 && (
                              <MenuItem disabled value="">No fuzzy suggestions</MenuItem>
                            )}
                            {r.suggestions.map((s, i) => {
                              const id = s.masterId || s.vendorRowId || `s${i}`;
                              return (
                                <MenuItem key={id} value={`map:${id}`}>
                                  {s.standardizedName}
                                  <Chip size="small" label={`${(s.score * 100).toFixed(0)}% ${s.source}`} sx={{ ml: 1 }} />
                                </MenuItem>
                              );
                            })}
                            <MenuItem value="addNew" sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                              <AddIcon fontSize="small" sx={{ mr: 1 }} /> Add as new parameter…
                            </MenuItem>
                          </Select>
                        </FormControl>
                        {dec?.action === 'addNew' && (
                          <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                            New: <strong>{dec.newParam?.standardizedName}</strong> ({dec.newParam?.valueType})
                          </Alert>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={!!addNewDialog} onClose={() => setAddNewDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Add as new Parameter Master entry</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This creates a <strong>pending</strong> Parameter Suggestion (and vendor alias suggestion) that an admin must approve before it appears in the canonical Parameter Master.
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Standardized name" fullWidth required
              value={newParam.standardizedName} onChange={(e) => setNewParam({ ...newParam, standardizedName: e.target.value })} />
            <FormControl fullWidth>
              <Select value={newParam.valueType} onChange={(e) => setNewParam({ ...newParam, valueType: String(e.target.value) })}>
                {VALUE_TYPES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Unit (optional)" fullWidth
              value={newParam.unit} onChange={(e) => setNewParam({ ...newParam, unit: e.target.value })} />
            <TextField label="Category (optional)" fullWidth
              value={newParam.category} onChange={(e) => setNewParam({ ...newParam, category: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNewDialog(null)}>Cancel</Button>
          <Button onClick={confirmAddNew} variant="contained" color="warning" disabled={!newParam.standardizedName.trim()}>
            Queue as new
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MappingPreview;
